import React, { useCallback, useMemo } from 'react'
import {
  Slate,
  Editable,
  useSlateStatic,
  useSelected,
  useFocused,
  withReact,
  ReactEditor
} from 'slate-react'
import {
  Editor,
  Range,
  Point,
  createEditor,
  Element as SlateElement,
  Transforms
} from 'slate'
import { withHistory } from 'slate-history'
import { css } from '@emotion/css'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { Delete } from 'mdi-material-ui'
import { formatDate } from '@/@core/utils/utils'
import { CheckBox } from '@mui/icons-material'
import { getCode } from '@/@core/layouts/utils'

const PasteHtmlExample = React.forwardRef(
  (
    props: {
      value: any[]
      value1: string
      value2: string
      value3: string
      signature: string
      signature2: string
      signature3: string
      checked1: boolean
    },
    ref
  ) => {
    const [value, setValue] = React.useState(props.value1 ?? '')
    const [value2, setValue2] = React.useState(props.value2 ?? '')
    const [value3, setValue3] = React.useState(props.value3 ?? '')
    const [signature, setSignature] = React.useState(props.signature ?? '')
    const [signature2, setSignature2] = React.useState(props.signature2 ?? '')
    const [signature3, setSignature3] = React.useState(props.signature3 ?? '')
    const [checked, setChecked] = React.useState(props.checked1 ?? false)
    const renderElement = useCallback(
      (props: any) => (
        <Element
          {...props}
          value1={value}
          value2={value2}
          value3={value3}
          signature={signature}
          signature2={signature2}
          signature3={signature3}
          checked1={checked}
          setValue={setValue}
          setValue2={setValue2}
          setValue3={setValue3}
          setChecked={setChecked}
        />
      ),
      [value, value2, value3, checked, signature, signature2, signature3]
    )
    const renderLeaf = useCallback((props: any) => <Leaf {...props} />, [])
    const [key, setKey] = React.useState(0)
    const editor = useMemo(
      () => withTables(withHistory(withReact(createEditor()))),
      []
    )

    const data = React.useMemo(() => props.value, [props.value])

    // 获取历史记录
    const getHistory = () => {
      const history = editor.history

      //

      return history
    }

    React.useEffect(() => {
      setKey(Math.random())
    }, [data])

    React.useEffect(() => {
      setValue(props.value1)

      setValue2(props.value2)

      setValue3(props.value3)

      setChecked(props.checked1)
      setSignature(props.signature)
      setSignature2(props.signature2)
      setSignature3(props.signature3)
    }, [props])

    React.useImperativeHandle(ref, () => {
      return {
        editor,
        value,
        value2,
        value3,
        checked,
        getHistory,
        signature,
        signature2,
        signature3
      }
    })

    return (
      <Slate editor={editor} initialValue={data} key={key}>
        <Editable
          style={{
            padding: '20px',
            wordBreak: 'keep-all'
          }}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
        />
      </Slate>
    )
  }
)

const withTables = (editor: any) => {
  const { deleteBackward, deleteForward, insertBreak } = editor

  editor.deleteBackward = (unit: any) => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n: any) =>
          !Editor.isEditor(n) &&
          (SlateElement.isElement(n) as any) &&
          n.type === 'table-cell'
      })

      if (cell) {
        const [, cellPath] = cell
        const start = Editor.start(editor, cellPath)

        if (Point.equals(selection.anchor, start)) {
          return
        }
      }
    }

    deleteBackward(unit)
  }

  editor.deleteForward = (unit: any) => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n: any) =>
          !Editor.isEditor(n) &&
          (SlateElement.isElement(n) as any) &&
          n.type === 'table-cell'
      })

      if (cell) {
        const [, cellPath] = cell
        const end = Editor.end(editor, cellPath)

        if (Point.equals(selection.anchor, end)) {
          return
        }
      }
    }

    deleteForward(unit)
  }

  editor.insertBreak = () => {
    const { selection } = editor

    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: (n: any) =>
          !Editor.isEditor(n) &&
          (SlateElement.isElement(n) as any) &&
          n.type === 'table'
      })

      if (table) {
        return
      }
    }

    insertBreak()
  }

  return editor
}

const Image = ({ attributes, children, element }: any) => {
  const selected = useSelected()
  const focused = useFocused()

  return (
    <div {...attributes}>
      {children}

      <div
        contentEditable={false}
        className={css`
          position: relative;
        `}
      >
        <img
          src={element.url}
          className={css`
            display: block;
            max-width: 100%;
            max-height: 20em;
            box-shadow: ${selected && focused ? '0 0 0 3px #B4D5FF' : 'none'};
            width: 100%;
          `}
        />
      </div>
    </div>
  )
}

const Element = (props: any) => {
  const { attributes, children, element } = props
  const [value, setValue] = React.useState(props.value1 ?? '')
  const [value2, setValue2] = React.useState(props.value2 ?? '')
  const [value3, setValue3] = React.useState(props.value3 ?? '')
  const [checked, setChecked] = React.useState(props.checked1 ?? false)
  const [signature, setSignature] = React.useState(props.signature ?? '')
  const [signature2, setSignature2] = React.useState(props.signature2 ?? '')
  const [signature3, setSignature3] = React.useState(props.signature3 ?? '')

  React.useEffect(() => {
    if (
      element.type === 'radio-group' ||
      element.type === 'radio-group-eh' ||
      element.type === 'radio-group-α-thalassemia-eh' ||
      element.type === 'radio-group-β-thalassemia-eh'
    ) {
      setValue(props.value1)

      setValue2(props.value2)

      setValue3(props.value3)

      setChecked(props.checked1)
    }
    if (
      element.type === 'signature-footer' ||
      element.type === 'yunnan-footer'
    ) {
      setSignature(props.signature)
      setSignature2(props.signature2)
      setSignature3(props.signature3)
    }
  }, [props])

  switch (element.type) {
    case 'image':
      return <Image {...props} />
    case 'block-title':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '2px solid',
            borderColor: '#32B0C9'
          }}
        >
          <img
            className={css`
              width: 240px;
            `}
            src="/images/logos/genomelight_logo.png"
          />
        </div>
      )
    case 'block-title-yunnan':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '2px solid',
            borderColor: '#32B0C9',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <img
              className={css`
                width: 240px;
              `}
              src="/images/logos/云南1.png"
            />
          </div>

          <div>
            <h2
              className={css`
                font-size: 24px;
                text-align: center;
                padding: 5px;
                margin: 10px 0;
                display: flex;
                align-content: start;
                justify-content: center;
              `}
            >
              {'医学遗传科基因检测报告'}
            </h2>
          </div>

          <div>
            <img
              className={css`
                width: 80px;
              `}
              src="/images/logos/云南2.png"
            />
          </div>
        </div>
      )
    case 'yunnan-footer':
      return (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>报告人</span>

              {signature && (
                <span>
                  <img width={'80px'} height={'64px'} src={signature} />
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>审核者</span>

              {signature2 && (
                <span>
                  <img width={'120px'} height={'64px'} src={signature2} />
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>审核医师</span>

              {signature3 && (
                <span>
                  <img width={'120px'} height={'64px'} src={signature3} />
                </span>
              )}
            </div>

            <div>
              <span>报告时间</span>

              <span style={{ marginLeft: '10px' }}>{children}</span>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              marginTop: '10px'
            }}
          >
            云南省昆明市西山区金碧路
            157号云南省第一人民医院2号门诊楼8楼医学遗传科
          </div>
        </div>
      )
    case 'signature-footer':
      return (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>报告人</span>

              {signature && (
                <span>
                  <img width={'80px'} height={'64px'} src={signature} />
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>审核者</span>

              {signature2 && (
                <span>
                  <img width={'120px'} height={'64px'} src={signature2} />
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <span>审核医师</span>

              {signature3 && (
                <span>
                  <img width={'120px'} height={'64px'} src={signature3} />
                </span>
              )}
            </div>

            <div>
              <span>报告时间</span>

              <span style={{ marginLeft: '10px' }}>{children}</span>
            </div>
          </div>
        </div>
      )
    case 'block-sx-title':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '2px solid',
            borderColor: '#32B0C9'
          }}
        >
          <img
            className={css`
              width: 200px;
            `}
            src="/images/logos/genomelight_logo.png"
          />

          {/* <img
            className={css`
              width: 440px;
            `}
            src="/images/logos/sx2.png"
          /> */}
        </div>
      )
    case 'block-cq-title':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '2px solid',
            borderColor: '#32B0C9',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <img
              className={css`
                width: 240px;
              `}
              src=""
            />
          </div>

          <div>
            <h2
              className={css`
                font-size: 24px;
                text-align: center;
                padding: 5px;
                margin: 10px 0;
                display: flex;
                align-content: start;
                justify-content: center;
              `}
            >
              {'重庆医科大学附二医院'}
            </h2>
          </div>

          <div>
            <img
              className={css`
                width: 80px;
              `}
              src=""
            />
          </div>
        </div>
      )
    case 'block-ajtk-title':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '2px solid',
            borderColor: '#32B0C9',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <img
              className={css`
                width: 240px;
              `}
              src="/images/logos/艾吉泰康.png"
            />
          </div>

          <div>
            <h2
              className={css`
                font-size: 24px;
                text-align: center;
                padding: 5px;
                margin: 10px 0;
                display: flex;
                align-content: start;
                justify-content: center;
              `}
            >
              {'艾吉泰康'}
            </h2>
          </div>

          <div>
            <img
              className={css`
                width: 80px;
              `}
              src=""
            />
          </div>
        </div>
      )
    case 'block-patient-info':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            flexWrap: 'wrap'
          }}
        >
          {children}
        </div>
      )
    case 'block-gls-info':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            flexWrap: 'wrap'
          }}
        >
          {children}
        </div>
      )
    case 'box-center':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
          }}
        >
          {children}
        </div>
      )
    case 'box-center-column':
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            flexDirection: 'column'
          }}
        >
          {children}
        </div>
      )
    case 'block-gls-footer':
      return (
        <Grid container direction="row">
          {children}
        </Grid>
      )
    case 'box':
      return <Box>{children}</Box>
    case 'box-blackborder':
      return <Box border={'2px solid'}>{children}</Box>
    case 'box-blackdashedborder':
      return (
        <Box border={'1px dashed'} padding={2}>
          {children}
        </Box>
      )
    case 'table':
      return (
        <table>
          <tbody {...attributes}>{children}</tbody>
        </table>
      )
    case 'table-available':
      return (
        <table
          className={css`
            width: -moz-available;
            width: -webkit-fill-available;
            margin: 20px auto;
          `}
        >
          <tbody {...attributes}>{children}</tbody>
        </table>
      )
    case 'table-row':
      return <tr {...attributes}>{children}</tr>
    case 'table-cell':
      return (
        <td {...attributes} style={{ border: '1px solid', padding: '8px 5px' }}>
          {children}
        </td>
      )
    case 'table-cell-c12':
      return (
        <td
          {...attributes}
          colSpan={12}
          style={{ border: '1px solid', padding: '8px 5px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c11':
      return (
        <td
          {...attributes}
          colSpan={11}
          style={{ border: '1px solid', padding: '8px 5px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c9':
      return (
        <td
          {...attributes}
          colSpan={9}
          style={{ border: '1px solid', padding: '8px 5px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c6':
      return (
        <td
          {...attributes}
          colSpan={6}
          style={{ border: '1px solid', padding: '8px 5px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c7':
      return (
        <td
          {...attributes}
          colSpan={7}
          style={{ border: '1px solid', padding: '8px 5px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c7-2':
      return (
        <td
          {...attributes}
          colSpan={7}
          style={{ border: '1px solid', padding: '0' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c8-2':
      return (
        <td
          {...attributes}
          colSpan={8}
          style={{ border: '1px solid', padding: '0' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c9-2':
      return (
        <td
          {...attributes}
          colSpan={9}
          style={{ border: '1px solid', padding: '0' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-c4':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          colSpan={4}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-c7':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          colSpan={7}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r8':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={8}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c8':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          colSpan={8}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c4':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          colSpan={4}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r9':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={9}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r8':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={8}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r7':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={7}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r6':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={6}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r5':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={5}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r4':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={4}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r3':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={3}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r2':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={2}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-r1':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          rowSpan={1}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c2':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          colSpan={2}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c3':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          colSpan={3}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-c5':
      return (
        <td
          className={css`
            padding: 5px;
          `}
          colSpan={5}
          {...attributes}
          style={{ border: '1px solid black' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r7':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={7}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r6':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={6}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r5':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={5}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r4':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={4}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r3':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={3}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-r2':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          rowSpan={2}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-genetic-c2':
      return (
        <td
          className={css`
            background-color: rgb(236, 246, 244);
            padding: 5px;
          `}
          colSpan={2}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color':
      return (
        <td
          className={css`
            color: rgb(0, 137, 165);
          `}
          {...attributes}
          style={{ border: '1px solid', padding: '8px 4px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-c2':
      return (
        <td
          className={css`
            color: rgb(0, 137, 165);
          `}
          {...attributes}
          colSpan={2}
          style={{ border: '1px solid', padding: '8px 4px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-c3':
      return (
        <td
          className={css`
            color: rgb(0, 137, 165);
          `}
          {...attributes}
          colSpan={3}
          style={{ border: '1px solid', padding: '8px 4px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-c4':
      return (
        <td
          className={css`
            color: rgb(0, 137, 165);
          `}
          {...attributes}
          colSpan={4}
          style={{ border: '1px solid', padding: '8px 4px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-c5':
      return (
        <td
          className={css`
            color: rgb(0, 137, 165);
          `}
          {...attributes}
          colSpan={5}
          style={{ border: '1px solid', padding: '8px 4px' }}
        >
          {children}
        </td>
      )
    case 'table-cell-eh':
      return (
        <td {...attributes} style={{ border: '1px solid', padding: '8px 4px' }}>
          {children}
        </td>
      )
    case 'table-cell-color-genetic':
      return (
        <td
          className={css`
            background-color: rgb(193, 225, 218);
            padding: 5px;
          `}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-krd':
      return (
        <td
          className={css`
            background-color: rgb(94, 159, 165);
            padding: 5px;
          `}
          {...attributes}
          style={{ border: '1px solid black', color: 'white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-r2':
      return (
        <td
          className={css`
            background-color: rgb(193, 225, 218);
            padding: 5px;
          `}
          {...attributes}
          rowSpan={2}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-r2-krd':
      return (
        <td
          className={css`
            background-color: rgb(94, 159, 165);
            padding: 5px;
          `}
          {...attributes}
          rowSpan={2}
          style={{ border: '1px solid black', color: 'white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-c2-krd':
      return (
        <td
          className={css`
            background-color: rgb(94, 159, 165);
            padding: 5px;
          `}
          {...attributes}
          colSpan={2}
          style={{ border: '1px solid black', color: 'white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-c2':
      return (
        <td
          className={css`
            background-color: rgb(193, 225, 218);
            padding: 5px;
          `}
          colSpan={2}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'table-cell-color-genetic-c4':
      return (
        <td
          className={css`
            background-color: rgb(193, 225, 218);
            padding: 5px;
          `}
          colSpan={4}
          {...attributes}
          style={{ border: '1px solid white' }}
        >
          {children}
        </td>
      )
    case 'header-box':
      return (
        <div
          className={css`
            padding: 5px;
            border: 1px solid rgb(0, 137, 165);
            margin: 10px 0;
          `}
        >
          {children}
        </div>
      )
    case 'header2':
      return (
        <h2
          className={css`
            font-size: 24px;
            text-align: center;
            padding: 5px;
            background-color: rgb(233, 247, 254);
            border: 2px solid rgb(0, 137, 165);
            margin: 10px 0;
            color: rgb(0, 137, 165);
            display: flex;
            align-content: start;
            justify-content: center;
          `}
        >
          {children}
        </h2>
      )
    case 'header2-genetic':
      return (
        <h2
          className={css`
            font-size: 24px;
            text-align: center;
            padding: 5px;
            margin: 10px 0;
            display: flex;
            align-content: start;
            justify-content: center;
          `}
        >
          {children}
        </h2>
      )
    case 'header2-color':
      return (
        <h2
          className={css`
            font-size: 24px;
            text-align: center;
            padding: 5px;
            margin: 10px 0;
            display: flex;
            align-content: start;
            justify-content: center;
            color: rgb(0, 137, 165);
          `}
        >
          {children}
        </h2>
      )
    case 'h3-genetic':
      return (
        <h4
          style={{
            fontSize: '22px',
            margin: '15px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            fontWeight: 600
          }}
        >
          {children}
        </h4>
      )
    case 'header3':
      return (
        <h3
          className={css`
            font-size: 20px;
            background-color: rgb(233, 247, 254);
            border: 2px solid rgb(0, 137, 165);
            color: rgb(0, 137, 165);
            margin: 15px 0;
            display: flex;
            align-items: start;
            justify-content: start;
          `}
        >
          {children}
        </h3>
      )
    case 'header3-krd':
      return (
        <h3
          className={css`
            font-size: 20px;
            background-color: rgb(94, 159, 165);
            color: white;
            padding: 10px 0;
            display: flex;
            align-items: start;
            justify-content: center;
          `}
        >
          {children}
        </h3>
      )
    case 'h4-genetic':
      return (
        <h4
          className={css`
            font-size: 18px;
            margin: 15px 0;
            display: flex;
            align-items: center;
            justify-content: start;
            font-weight: 600;
          `}
        >
          {children}
        </h4>
      )
    case 'h4-genetic-blue':
      return (
        <h4
          className={css`
            font-size: 18px;
            margin: 15px 0;
            display: flex;
            align-items: center;
            justify-content: start;
            font-weight: 600;
            color: #0060df;
          `}
        >
          {children}
        </h4>
      )
    case 'h5-genetic':
      return (
        <h4
          className={css`
            font-size: 16px;
            margin: 15px 0;
            display: flex;
            align-items: center;
            justify-content: start;
            font-weight: 600;
          `}
        >
          {children}
        </h4>
      )
    case 'header3-genetic-krd':
      return (
        <Typography
          component={'h3'}
          sx={{
            fontSize: '20px',
            color: 'rgb(94,159,165)',
            padding: '5px 0',
            paddingLeft: '5px',
            margin: '15px 0',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'start',
            '::before': {
              content: '""',
              display: 'inline-block',
              width: 8,
              height: '1.25rem',
              bgcolor: 'rgb(237,152,49)',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: '-10px'
            },
            lineHeight: '1.25em',
            position: 'relative',
            ml: '10px',
            py: 1
          }}
          className={'header3-genetic-krd'}
        >
          {children}
        </Typography>
      )
    case 'header3-genetic-krd2':
      return (
        <Typography
          component={'h3'}
          sx={{
            fontSize: '20px',
            bgcolor: 'rgb(94,159,165)',
            color: 'white',
            padding: '5px 0',
            paddingLeft: '5px',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'start'
          }}
        >
          {children}
        </Typography>
      )
    case 'header3-yunnan-krd2':
      return (
        <Typography
          component={'h3'}
          sx={{
            fontSize: '20px',
            bgcolor: 'rgb(54,95,145)',
            color: 'white',
            padding: '5px 0',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center'
          }}
        >
          {children}
        </Typography>
      )
    case 'yunnan-krd2':
      return (
        <Typography
          sx={{
            fontSize: '20px',
            bgcolor: 'rgb(54,95,145)',
            color: 'white',
            padding: '5px 0',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'center'
          }}
        >
          {children}
        </Typography>
      )
    case 'header3-genetic':
      return (
        <h3
          style={{
            fontSize: '20px',
            backgroundColor: 'rgb(58, 159, 139)',
            color: 'white',
            padding: '5px 0',
            paddingLeft: '5px',
            margin: '15px 0',
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'start'
          }}
          className={'header3-genetic'}
        >
          {children}
        </h3>
      )
    case 'header4':
      return (
        <h3
          className={css`
            font-size: 18px;
            color: rgb(0, 137, 165);
            display: flex;
            align-items: center;
          `}
        >
          {children}
        </h3>
      )
    case 'p3':
      return (
        <p
          className={css`
            width: 33%;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center':
      return (
        <p
          className={css`
            text-align: center;
            width: 100%;
            margin-bottom: 0;
            padding: 4px 0;
            word-break: break-all;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center-min-100':
      return (
        <p
          className={css`
            text-align: center;
            width: 100%;
            margin-bottom: 0;
            padding: 4px 0;
            word-break: break-all;
            min-width: 100px;
          `}
        >
          {children}
        </p>
      )
    case 'display-none':
      return (
        <p
          className={css`
            display: none;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center-padding':
      return (
        <p
          className={css`
            text-align: center;
            width: 100%;
            margin-bottom: 0;
            padding: 0 80px;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center-bold':
      return (
        <p
          className={css`
            text-align: center;
            width: 100%;
            margin-bottom: 0;
            font-weight: 600;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center-max':
      return (
        <p
          className={css`
            text-align: center;
            max-width: 300px;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'p3-center-max-180':
      return (
        <p
          className={css`
            text-align: center;
            max-width: 300px;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'p3-max':
      return (
        <p
          className={css`
            max-width: 300px;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'p4-center':
      return (
        <p
          className={css`
            text-align: center;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'p5':
      return (
        <p
          className={css`
            padding-left: 30px;
            font-weight: 600;
          `}
        >
          {children}
        </p>
      )
    case 'p6':
      return (
        <p
          className={css`
            padding-left: 60px;
            font-weight: 600;
          `}
        >
          {children}
        </p>
      )
    case 'p8':
      return (
        <p
          className={css`
            padding-left: 60px;
          `}
        >
          {children}
        </p>
      )
    case 'p9':
      return (
        <p
          className={css`
            padding-left: 30px;
            margin: 0;
          `}
        >
          {children}
        </p>
      )
    case 'p7':
      return (
        <p
          className={css`
            margin-bottom: 0;
            padding: 5px 0;
            word-break: break-word;
          `}
        >
          {children}
        </p>
      )
    case 'p7-HighlightText':
      return (
        <div
          className={css`
            margin-bottom: 0;
            padding: 5px 0;
            word-break: break-word;
          `}
          dangerouslySetInnerHTML={{ __html: element.children[0].text }}
        />
      )
    case 'p7-color-bold':
      return (
        <p
          className={css`
            margin-bottom: 0;
            padding: 5px 0;
            font-size: 18px;
            font-weight: 600;
            word-break: break-word;
            color: rgba(227, 110, 39);
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value':
      return (
        <p
          className={css`
            width: 22%;
            margin: 0;
            padding: 5px;
            background-color: rgb(236, 246, 244);
            border: 1px solid white;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-key':
      return (
        <p
          className={css`
            width: 11%;
            margin: 0;
            padding: 5px;
            background-color: rgb(236, 246, 244);
            border: 1px solid white;
          `}
        >
          {children}
        </p>
      )
    case 'p3-dashed-20':
      return (
        <p
          className={css`
            width: 20%;
            margin: 0;
            padding: 5px;
            border: 1px dashed black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value-dashed':
      return (
        <p
          className={css`
            width: 22%;
            margin: 0;
            padding: 5px;
            border: 1px dashed black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value-dashed-13':
      return (
        <p
          className={css`
            width: 13.33%;
            margin: 0;
            padding: 5px;
            border: 1px solid black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value-dashed-88':
      return (
        <p
          className={css`
            width: 88%;
            margin: 0;
            padding: 5px;
            border: 1px dashed black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value-dashed-80':
      return (
        <p
          className={css`
            width: 80%;
            margin: 0;
            padding: 5px;
            border: 1px solid black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-key-dashed':
      return (
        <p
          className={css`
            width: 11%;
            margin: 0;
            padding: 5px;
            border: 1px dashed black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-key-dashed-20':
      return (
        <p
          className={css`
            width: 20%;
            margin: 0;
            padding: 5px;
            border: 1px solid black;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-key-33':
      return (
        <p
          className={css`
            width: 33%;
            margin: 0;
            padding: 10px 0;
            background-color: rgb(58, 159, 139);
            border: 1px solid white;
            color: white;
            text-align: center;
          `}
        >
          {children}
        </p>
      )
    case 'p3-genetic-value-66':
      return (
        <p
          className={css`
            width: 66%;
            margin: 0;
            padding: 10px 0;
            background-color: rgb(236, 246, 244);
            border: 1px solid white;
            text-align: center;
          `}
        >
          {children}
        </p>
      )
    case 'p4':
      return (
        <p
          className={css`
            max-width: 25%;
            flex-basis: 25%;
          `}
        >
          {children}
        </p>
      )
    case 'p4-genetic-value':
      return (
        <p
          className={css`
            word-break: initial;
          `}
        >
          {children}
        </p>
      )
    case 'p5-genetic-value':
      return (
        <p
          className={css`
            word-break: break-word;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'paragraph':
      return (
        <p
          className={css`
            margin-bottom: 0;
            line-height: 1.75rem;
            word-break: break-all;
          `}
        >
          {children}
        </p>
      )
    case 'paragraph-noBreak':
      return (
        <p
          className={css`
            margin-bottom: 0;
            line-height: 1.75rem;
            word-break: keep-all;
          `}
        >
          {children}
        </p>
      )
    case 'paragraph-omim':
      return (
        <p
          className={css`
            margin-bottom: 0;
            line-height: 1.75rem;
            word-break: break-all;
          `}
          style={{
            maxWidth: 300
          }}
        >
          {children}
        </p>
      )
    case 'paragraph-gene':
      return (
        <p
          className={css`
            margin-bottom: 0;
            line-height: 1.75rem;
            word-break: break-all;
          `}
          style={{
            maxWidth: 850
          }}
        >
          {children}
        </p>
      )
    case 'paragraph-2':
      return (
        <p
          className={css`
            margin-bottom: 0;
            line-height: 1.75rem;
          `}
        >
          {children}
        </p>
      )
    case 'HighlightText':
      return (
        <div dangerouslySetInnerHTML={{ __html: element.children[0].text }} />
      )
    case 'paragraph-color':
      return (
        <p
          className={css`
            margin-bottom: 0;
            padding: 4px;
            background-color: rgb(236, 246, 244);
          `}
        >
          {children}
        </p>
      )
    case 'paragraph-small':
      return (
        <p
          className={css`
            font-weight: 500;
            font-size: 14px;
            padding: 4px 2px;
            margin-bottom: 0;
          `}
        >
          {children}
        </p>
      )
    case 'span':
      return (
        <span
          className={css`
            font-weight: 500;
            font-size: 16px;
          `}
        >
          {children}
        </span>
      )
    case 'span-start':
      return (
        <span
          className={css`
            font-weight: 500;
            font-size: 16px;
            padding-left: 30px;
            margin: 0;
          `}
        >
          {children}
        </span>
      )
    case 'span-blod':
      return (
        <span
          className={css`
            font-weight: 600;
            font-size: 16px;
          `}
        >
          {children}
        </span>
      )
    case 'span-red':
      return (
        <span
          className={css`
            color: red;
          `}
        >
          {children}
        </span>
      )
    case 'span-blod-red':
      return (
        <span
          className={css`
            font-weight: 600;
            font-size: 16px;
            color: red;
          `}
        >
          {children}
        </span>
      )
    case 'g2':
      return (
        <Grid item xs={12} md={6}>
          <Typography>{children}</Typography>
        </Grid>
      )
    case 'radio-group':
      return (
        <Grid item xs={12} md={6} sx={{ display: 'inline-block' }}>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onClick={(e: any) => {
                //

                if (e.target.outerText) {
                  setValue(e.target.outerText)

                  props.setValue && props.setValue(e.target.outerText)
                }
              }}
              value={value}
            >
              <FormControlLabel
                value="疾病高风险"
                control={<Radio />}
                label="疾病高风险"
              />

              <FormControlLabel
                value="疾病中风险"
                control={<Radio />}
                label="疾病中风险"
              />

              <FormControlLabel
                value="携带者"
                control={<Radio />}
                label="携带者"
              />

              <FormControlLabel value="阴性" control={<Radio />} label="阴性" />
            </RadioGroup>
          </FormControl>
        </Grid>
      )
    case 'radio-group-eh':
      return (
        <Grid item xs={12} md={6} sx={{ display: 'inline-block' }}>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onClick={(e: any) => {
                //

                if (e.target.outerText) {
                  setValue(e.target.outerText)

                  props.setValue && props.setValue(e.target.outerText)
                }
              }}
              value={value}
            >
              <FormControlLabel
                value="High risk of disease"
                control={<Radio />}
                label="High risk of disease"
              />

              <FormControlLabel
                value="Risk in disease"
                control={<Radio />}
                label="Risk in disease"
              />

              <FormControlLabel
                value="Carrier"
                control={<Radio />}
                label="Carrier"
              />

              <FormControlLabel
                value="Negative"
                control={<Radio />}
                label="Negative"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )
    case 'radio-group-α-thalassemia-eh':
      return (
        <Grid maxWidth={500} item sx={{ display: 'inline-block' }}>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onClick={(e: any) => {
                //

                if (e.target.outerText) {
                  setValue2(e.target.outerText)

                  props.setValue2 && props.setValue2(e.target.outerText)
                }
              }}
              value={value2}
            >
              <FormControlLabel
                value="Static carrier"
                control={<Radio />}
                label="Static carrier"
              />

              <FormControlLabel
                value="Standard carrier"
                control={<Radio />}
                label="Standard carrier"
              />

              <FormControlLabel
                value="HbH disease"
                control={<Radio />}
                label="HbH disease"
              />

              <FormControlLabel
                value="HbBart's fetal edema syndrome"
                control={<Radio />}
                label="HbBart's fetal edema syndrome"
              />

              <FormControlLabel
                value="Negative"
                control={<Radio />}
                label="Negative"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )
    case 'radio-group-β-thalassemia-eh':
      return (
        <Grid maxWidth={500} item sx={{ display: 'inline-block' }}>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              onClick={(e: any) => {
                //

                if (e.target.outerText) {
                  setValue3(e.target.outerText)

                  props.setValue3 && props.setValue3(e.target.outerText)
                }
              }}
              value={value3}
            >
              <FormControlLabel
                value="Homozygote"
                control={<Radio />}
                label="Homozygote"
              />

              <FormControlLabel
                value="Double heterozygote"
                control={<Radio />}
                label="Double heterozygote"
              />

              <FormControlLabel
                value="Carrier"
                control={<Radio />}
                label="Carrier"
              />

              <FormControlLabel
                value="Negative"
                control={<Radio />}
                label="Negative"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )
    case 'check-box':
      return (
        <FormControlLabel
          checked={checked}
          onClick={(e: any) => {
            setChecked(!props.checked)

            props.setChecked && props.setChecked(!props.checked)
          }}
          control={
            <Checkbox
              size="small"
              onChange={(e) => {
                setChecked(e.target.checked)

                props.setChecked && props.setChecked(e.target.checked)
              }}
            />
          }
          label="EDTA抗凝血"
        />
      )
    case 'divider':
      return <Divider />
    default:
      return <p {...attributes}>{children}</p>
  }
}

const Leaf = ({ attributes, children, leaf }: any) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  return <span {...attributes}>{children}</span>
}

export default PasteHtmlExample
